import { useState, useEffect } from 'react';
import { useTimeTracker } from '@/hooks/useTimeTracker';
import { formatDuration } from '@/lib/utils';

export const TimeTrackerCard = () => {
  const {
    projects,
    activeRecord,
    isLoading,
    isProcessing,
    clockIn,
    clockInWithoutProject,
    assignProject,
    clockOut,
    getCurrentWorkingMinutes,
  } = useTimeTracker();

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [workingMinutes, setWorkingMinutes] = useState(0);
  const [showProjectSelect, setShowProjectSelect] = useState(false);

  // リアルタイム時計
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      if (activeRecord) {
        setWorkingMinutes(getCurrentWorkingMinutes());
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [activeRecord, getCurrentWorkingMinutes]);

  const handleClockIn = async () => {
    if (!selectedProjectId) return;
    try {
      await clockIn(selectedProjectId);
      setSelectedProjectId('');
    } catch (error) {
      console.error('Clock in error:', error);
    }
  };

  /**
   * 🆕 プロジェクト未選択で出勤
   */
  const handleClockInWithoutProject = async () => {
    try {
      await clockInWithoutProject();
      setShowProjectSelect(false);
    } catch (error) {
      console.error('Clock in without project error:', error);
    }
  };

  /**
   * 🆕 出勤中にプロジェクトを紐付け
   */
  const handleAssignProject = async () => {
    if (!activeRecord || !selectedProjectId) return;
    try {
      await assignProject(activeRecord.id, selectedProjectId);
      setSelectedProjectId('');
      setShowProjectSelect(false);
    } catch (error) {
      console.error('Assign project error:', error);
    }
  };

  const handleClockOut = async () => {
    try {
      await clockOut();
    } catch (error) {
      console.error('Clock out error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="card animate-pulse">
        <div className="h-32 bg-gray-200 rounded-lg mb-4"></div>
        <div className="h-16 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {/* Glassmorphism Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-primary-400/5 to-transparent rounded-2xl blur-3xl"></div>
      
      <div className="relative card border border-white/20 backdrop-blur-sm">
        {/* Live Clock */}
        <div className="text-center mb-6">
          <div className="text-5xl font-bold text-gray-900 mb-2 tracking-tight">
            {currentTime.toLocaleTimeString('ja-JP', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </div>
          <div className="text-sm text-gray-500">
            {currentTime.toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              weekday: 'long',
            })}
          </div>
        </div>

        {/* Status Display */}
        <div className="mb-6 p-6 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200">
          <p className="text-sm text-gray-600 mb-2">現在のステータス</p>
          
          {activeRecord ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
                </div>
                <span className="text-3xl font-bold text-green-600">出勤中</span>
              </div>
              
              {/* 🆕 プロジェクト未割当て警告 */}
              {!activeRecord.projectId && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-yellow-800">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="font-medium">プロジェクトが未割当てです</span>
                  </div>
                  <button
                    onClick={() => setShowProjectSelect(true)}
                    className="mt-2 w-full py-2 px-4 bg-yellow-500 text-white text-sm font-medium rounded-lg hover:bg-yellow-600 transition-colors"
                  >
                    プロジェクトを追加
                  </button>
                </div>
              )}
              
              {activeRecord.project && (
                <div className="flex items-center gap-2 text-sm text-gray-700 bg-white px-4 py-2 rounded-lg">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="font-medium">
                    {activeRecord.project.code && `${activeRecord.project.code} - `}
                    {activeRecord.project.name}
                  </span>
                </div>
              )}
              
              <div className="text-center py-4 bg-white rounded-lg border-2 border-green-200">
                <p className="text-sm text-gray-600 mb-1">稼働時間</p>
                <p className="text-4xl font-bold text-green-600 tabular-nums">
                  {formatDuration(workingMinutes)}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-200 rounded-full mb-3">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-gray-400">未出勤</p>
            </div>
          )}
        </div>

        {/* 🆕 出勤中のプロジェクト割当て */}
        {activeRecord && !activeRecord.projectId && showProjectSelect && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-blue-900">プロジェクトを選択</p>
              <button
                onClick={() => setShowProjectSelect(false)}
                className="text-blue-600 hover:text-blue-800"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-4 py-2 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              disabled={isProcessing}
            >
              <option value="">プロジェクトを選択...</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code && `${project.code} - `}
                  {project.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleAssignProject}
              disabled={!selectedProjectId || isProcessing}
              className="w-full py-2 px-4 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isProcessing ? '処理中...' : 'プロジェクトを追加'}
            </button>
          </div>
        )}

        {/* Project Selection (when not clocked in) */}
        {!activeRecord && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              プロジェクトを選択
            </label>
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              disabled={isProcessing}
            >
              <option value="">プロジェクトを選択してください</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.code && `${project.code} - `}
                  {project.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Action Button */}
        <div className="space-y-3">
          {activeRecord ? (
            <button
              onClick={handleClockOut}
              disabled={isProcessing}
              className="w-full py-4 px-6 bg-gradient-to-r from-red-500 to-red-600 text-white text-xl font-bold rounded-xl hover:from-red-600 hover:to-red-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-red-500/30"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  処理中...
                </span>
              ) : (
                '退勤'
              )}
            </button>
          ) : (
            <>
              {/* 🆕 プロジェクト選択で出勤 */}
              <button
                onClick={handleClockIn}
                disabled={!selectedProjectId || isProcessing}
                className="w-full py-4 px-6 bg-gradient-to-r from-green-500 to-green-600 text-white text-xl font-bold rounded-xl hover:from-green-600 hover:to-green-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-green-500/30"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    処理中...
                  </span>
                ) : (
                  '出勤'
                )}
              </button>
              
              {/* 🆕 プロジェクトなしで出勤 */}
              <button
                onClick={handleClockInWithoutProject}
                disabled={isProcessing}
                className="w-full py-3 px-6 bg-white text-green-600 text-base font-semibold rounded-xl border-2 border-green-500 hover:bg-green-50 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isProcessing ? '処理中...' : 'プロジェクトなしで出勤'}
              </button>
              <p className="text-xs text-gray-500 text-center">※ プロジェクトは後から追加できます</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
